import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import proxy from "../../proxy";
import {
  Box,
  Container,
  Heading,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  HStack,
  Badge,
  useDisclosure,
  useToast,
  Text,
  VStack,
  Spinner,
  Center,
  Input,
  InputGroup,
  InputLeftElement,
  Stat,
  StatLabel,
  StatNumber,
  SimpleGrid,
} from "@chakra-ui/react";
import {
  EditIcon,
  DeleteIcon,
  AddIcon,
  ArrowBackIcon,
  SearchIcon,
} from "@chakra-ui/icons";
import DividendModal from "../../components/Admin/DividendModal";

const AdminDividends = () => {
  const [dividends, setDividends] = useState([]);
  const [filteredDividends, setFilteredDividends] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDividend, setSelectedDividend] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const navigate = useNavigate();

  // Fetch dividends
  const fetchDividends = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${proxy}server_mts/admin/GetAllDividends.php`);
      setDividends(response.data);
      setFilteredDividends(response.data);
    } catch (error) {
      toast({
        title: "Error loading dividends",
        description: error.message,
        status: "error",
        duration: 3000,
        position: "top",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDividends();
  }, []);

  // Search filter
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredDividends(dividends);
    } else {
      const filtered = dividends.filter(
        (d) =>
          d.ticker.toLowerCase().includes(searchQuery.toLowerCase()) ||
          d.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredDividends(filtered);
    }
  }, [searchQuery, dividends]);

  // Add dividend
  const handleAdd = () => {
    setSelectedDividend(null);
    onOpen();
  };

  // Edit dividend
  const handleEdit = (dividend) => {
    setSelectedDividend(dividend);
    onOpen();
  };

  // Delete dividend
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this dividend?")) {
      return;
    }

    try {
      await axios.post(`${proxy}server_mts/admin/DeleteDividend.php`, { id });
      toast({
        title: "Dividend deleted successfully",
        status: "success",
        duration: 2000,
        position: "top",
      });
      fetchDividends();
    } catch (error) {
      toast({
        title: "Error deleting dividend",
        description: error.message,
        status: "error",
        duration: 3000,
        position: "top",
      });
    }
  };

  // Save dividend
  const handleSave = () => {
    onClose();
    setTimeout(() => {
      fetchDividends();
    }, 200);
  };

  // Calculate stats
  const totalDividends = dividends.reduce((sum, d) => sum + Number(d.amount), 0);
  const totalTax = dividends.reduce((sum, d) => sum + Number(d.tax), 0);
  const netDividends = totalDividends - totalTax;

  if (isLoading) {
    return (
      <Box minH="100vh" bg="gray.900">
        <Center minH="100vh">
          <VStack spacing={4}>
            <Spinner size="xl" color="teal.400" thickness="4px" />
            <Text color="gray.400">Loading dividends...</Text>
          </VStack>
        </Center>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="gray.900">
      {/* Header */}
      <Box
        bg="linear-gradient(135deg, rgba(26,32,44,0.98), rgba(45,55,72,0.95))"
        borderBottom="1px solid"
        borderColor="whiteAlpha.200"
        py={4}
        px={6}
        boxShadow="0 4px 20px rgba(0,0,0,0.3)"
      >
        <Container maxW="1400px">
          <HStack justify="space-between">
            <HStack spacing={4}>
              <IconButton
                icon={<ArrowBackIcon />}
                variant="ghost"
                colorScheme="teal"
                onClick={() => navigate("/admin/dashboard")}
                aria-label="Back to dashboard"
              />
              <VStack align="start" spacing={0}>
                <Heading
                  size="md"
                  bgGradient="linear(to-r, teal.300, cyan.400)"
                  bgClip="text"
                >
                  💰 Dividend Management
                </Heading>
                <Text fontSize="xs" color="gray.500">
                  Track dividend payments and tax deductions
                </Text>
              </VStack>
            </HStack>

            <Button
              leftIcon={<AddIcon />}
              colorScheme="teal"
              onClick={handleAdd}
              size="sm"
            >
              Add Dividend
            </Button>
          </HStack>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxW="1400px" py={6}>
        <VStack spacing={6} align="stretch">
          {/* Stats Overview */}
          <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={4}>
            <Box
              p={4}
              bg="rgba(26,32,44,0.6)"
              borderRadius="lg"
              border="1px solid"
              borderColor="whiteAlpha.200"
            >
              <Stat>
                <StatLabel color="gray.400" fontSize="xs">
                  Total Dividends
                </StatLabel>
                <StatNumber color="green.300" fontSize="2xl">
                  ${totalDividends.toFixed(2)}
                </StatNumber>
              </Stat>
            </Box>

            <Box
              p={4}
              bg="rgba(26,32,44,0.6)"
              borderRadius="lg"
              border="1px solid"
              borderColor="whiteAlpha.200"
            >
              <Stat>
                <StatLabel color="gray.400" fontSize="xs">
                  Total Tax
                </StatLabel>
                <StatNumber color="red.300" fontSize="2xl">
                  ${totalTax.toFixed(2)}
                </StatNumber>
              </Stat>
            </Box>

            <Box
              p={4}
              bg="rgba(26,32,44,0.6)"
              borderRadius="lg"
              border="1px solid"
              borderColor="whiteAlpha.200"
            >
              <Stat>
                <StatLabel color="gray.400" fontSize="xs">
                  Net Dividends
                </StatLabel>
                <StatNumber color="teal.300" fontSize="2xl">
                  ${netDividends.toFixed(2)}
                </StatNumber>
              </Stat>
            </Box>
          </SimpleGrid>

          {/* Search */}
          <HStack justify="space-between" spacing={4}>
            <InputGroup maxW="400px">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search by ticker or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                bg="whiteAlpha.100"
                border="1px solid"
                borderColor="whiteAlpha.300"
                _hover={{ borderColor: "whiteAlpha.400" }}
                _focus={{ borderColor: "teal.400" }}
                color="white"
                size="sm"
              />
            </InputGroup>

            <Badge colorScheme="teal" fontSize="sm" px={3} py={1}>
              {filteredDividends.length} dividend{filteredDividends.length !== 1 ? "s" : ""}
            </Badge>
          </HStack>

          {/* Table */}
          <Box
            bg="linear-gradient(135deg, rgba(26,32,44,0.97), rgba(45,55,72,0.97))"
            borderRadius="xl"
            border="1px solid"
            borderColor="whiteAlpha.200"
            overflow="hidden"
            boxShadow="0 10px 30px rgba(0,0,0,0.3)"
          >
            {filteredDividends.length === 0 ? (
              <Center py={12}>
                <VStack spacing={3}>
                  <Text fontSize="4xl">📭</Text>
                  <Text color="gray.400" fontSize="lg">
                    {searchQuery ? "No dividends found" : "No dividends yet"}
                  </Text>
                  <Text color="gray.500" fontSize="sm">
                    {searchQuery
                      ? "Try a different search term"
                      : "Click 'Add Dividend' to get started"}
                  </Text>
                </VStack>
              </Center>
            ) : (
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead bg="whiteAlpha.50">
                    <Tr>
                      <Th color="teal.300" borderColor="whiteAlpha.200">Date</Th>
                      <Th color="teal.300" borderColor="whiteAlpha.200">Ticker</Th>
                      <Th color="teal.300" borderColor="whiteAlpha.200">Description</Th>
                      <Th color="teal.300" borderColor="whiteAlpha.200" isNumeric>Amount</Th>
                      <Th color="teal.300" borderColor="whiteAlpha.200" isNumeric>Tax</Th>
                      <Th color="teal.300" borderColor="whiteAlpha.200" isNumeric>Net</Th>
                      <Th color="teal.300" borderColor="whiteAlpha.200" textAlign="right">Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredDividends.map((dividend) => (
                      <Tr
                        key={dividend.id}
                        _hover={{ bg: "whiteAlpha.50" }}
                        transition="0.2s"
                      >
                        <Td color="gray.300" borderColor="whiteAlpha.100">
                          {new Date(dividend.date).toLocaleDateString()}
                        </Td>
                        <Td borderColor="whiteAlpha.100">
                          <Badge colorScheme="purple" fontSize="xs" fontWeight="bold">
                            {dividend.ticker}
                          </Badge>
                        </Td>
                        <Td color="white" fontSize="xs" borderColor="whiteAlpha.100" maxW="300px" isTruncated>
                          {dividend.description}
                        </Td>
                        <Td color="green.300" fontWeight="bold" borderColor="whiteAlpha.100" isNumeric>
                          ${Number(dividend.amount).toFixed(4)}
                        </Td>
                        <Td color="red.300" fontWeight="bold" borderColor="whiteAlpha.100" isNumeric>
                          ${Number(dividend.tax).toFixed(4)}
                        </Td>
                        <Td color="teal.300" fontWeight="bold" borderColor="whiteAlpha.100" isNumeric>
                          ${Number(dividend.net_amount).toFixed(4)}
                        </Td>
                        <Td borderColor="whiteAlpha.100" textAlign="right">
                          <HStack spacing={2} justify="flex-end">
                            <IconButton
                              icon={<EditIcon />}
                              size="sm"
                              colorScheme="blue"
                              variant="ghost"
                              onClick={() => handleEdit(dividend)}
                              aria-label="Edit dividend"
                            />
                            <IconButton
                              icon={<DeleteIcon />}
                              size="sm"
                              colorScheme="red"
                              variant="ghost"
                              onClick={() => handleDelete(dividend.id)}
                              aria-label="Delete dividend"
                            />
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </Box>
        </VStack>
      </Container>

      {/* Modal */}
      <DividendModal
        isOpen={isOpen}
        onClose={onClose}
        dividend={selectedDividend}
        onSave={handleSave}
      />
    </Box>
  );
};

export default AdminDividends;